import React, { useMemo, useState, useCallback } from "react";
import {
  Grid,
  Box,
  Typography,
  Chip,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  useGetStaffDashboardQuery,
  useGetStaffClassRecordsQuery,
  useGetStaffClassStudentsQuery,
  useGetStaffTimetableQuery,
  useGetUserQuery,
  useStoreStaffPlannerMutation,
  useSubmitStaffPlannerMutation,
  useCreateAttendanceRecordMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SchoolIcon from "@mui/icons-material/School";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MessageIcon from "@mui/icons-material/Message";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ListAltIcon from "@mui/icons-material/ListAlt";

/* ------------------------------------------------------------------ */
/*  DashCard                                                          */
/* ------------------------------------------------------------------ */
const DashCard: React.FC<{
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  noPadding?: boolean;
}> = ({ children, title, icon, action, noPadding }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {title && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {icon && (
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  display: "flex",
                  "& svg": { fontSize: 18 },
                }}
              >
                {icon}
              </Box>
            )}
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ p: noPadding ? 0 : 2.5 }}>{children}</Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
const greetingByHour = (): string => {
  const h = dayjs().hour();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const formatTime = (t: string | undefined) => {
  if (!t) return "";
  const [hour, minute] = t.split(":").map(Number);
  const d = new Date();
  d.setHours(hour, minute);
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(d);
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */
const StaffDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  // ---- Current user / staffId ----
  const { data: userData } = useGetUserQuery();
  const staffId = useMemo(() => {
    const user = userData?.data as any;
    return user?.staffId ? String(user.staffId) : "";
  }, [userData]);

  // ---- Dashboard data ----
  const {
    data: dashboardData,
    isLoading: dashLoading,
  } = useGetStaffDashboardQuery({ staffId }, { skip: !staffId });

  const dash: any = dashboardData?.data ?? {};
  const todayClasses: any[] = dash.todayClasses ?? [];
  const pendingPlanners: any[] = dash.pendingPlanners ?? [];
  const staffName: string = dash.staffName ?? "";
  const statsStudents: number = dash.studentCount ?? 0;
  const statsPending: number = pendingPlanners.length;
  const statsMessages: number = dash.messageCount ?? 0;

  // ---- Class records ----
  const [recordFilter, setRecordFilter] = useState<string>("today");
  const {
    data: classRecordsData,
    isLoading: recordsLoading,
  } = useGetStaffClassRecordsQuery(
    { staffId, filter: recordFilter },
    { skip: !staffId }
  );
  const classRecords: any[] = useMemo(
    () => ((classRecordsData?.data as any[]) ?? []).slice(0, 10),
    [classRecordsData]
  );

  // ---- Timetable ----
  const weekStart = dayjs().startOf("week").format("YYYY-MM-DD");
  const {
    data: timetableData,
    isLoading: timetableLoading,
  } = useGetStaffTimetableQuery(
    { staffId, weekStart },
    { skip: !staffId }
  );
  const timetable: any = timetableData?.data ?? {};

  // ---- Planner state ----
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [plannerForm, setPlannerForm] = useState({
    schoolFocus: "",
    assignedHomework: "",
    homeworkDone: "",
    challenges: "",
    notes: "",
    futurePlan: "",
    sendReminder: false,
    reminderMethod: "Email",
  });

  const [storePlanner] = useStoreStaffPlannerMutation();
  const [submitPlanner] = useSubmitStaffPlannerMutation();

  // ---- Attendance modal ----
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendanceClass, setAttendanceClass] = useState<any>(null);
  const {
    data: classStudentsData,
    isLoading: studentsLoading,
  } = useGetStaffClassStudentsQuery(
    { staffId, classId: attendanceClass?.id ?? "" },
    { skip: !attendanceClass?.id }
  );
  const classStudents: any[] = useMemo(
    () => (classStudentsData?.data as any[]) ?? [],
    [classStudentsData]
  );
  const [attendanceMap, setAttendanceMap] = useState<Record<string, string>>({});
  const [createAttendance] = useCreateAttendanceRecordMutation();

  // ---- Handlers ----
  const openPlannerFor = useCallback((cls: any) => {
    setSelectedClass(cls);
    setPlannerForm({
      schoolFocus: "",
      assignedHomework: "",
      homeworkDone: "",
      challenges: "",
      notes: "",
      futurePlan: "",
      sendReminder: false,
      reminderMethod: "Email",
    });
    setPlannerOpen(true);
  }, []);

  const handleSavePlanner = async (submit: boolean) => {
    try {
      const payload = {
        classId: selectedClass?.id,
        staffId,
        ...plannerForm,
      };
      await storePlanner(payload).unwrap();
      if (submit) {
        await submitPlanner({ classId: selectedClass?.id, staffId }).unwrap();
        showMessage("Planner submitted successfully");
      } else {
        showMessage("Draft saved");
      }
      setPlannerOpen(false);
    } catch {
      showErrorMessage("Failed to save planner");
    }
  };

  const openAttendanceFor = useCallback((cls: any) => {
    setAttendanceClass(cls);
    setAttendanceMap({});
    setAttendanceOpen(true);
  }, []);

  const handleSaveAttendance = async () => {
    try {
      await createAttendance({
        classId: attendanceClass?.id,
        staffId,
        records: Object.entries(attendanceMap).map(([studentId, status]) => ({
          studentId,
          status,
        })),
      }).unwrap();
      showMessage("Attendance saved");
      setAttendanceOpen(false);
    } catch {
      showErrorMessage("Failed to save attendance");
    }
  };

  // ---- Loading skeleton ----
  if (dashLoading && staffId) {
    return (
      <Box p={2}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3, mb: 2 }} animation="wave" />
        <Grid container spacing={2}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} md={6} lg={3} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} animation="wave" />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* ============================================================ */}
      {/*  SECTION 1: WELCOME BANNER                                   */}
      {/* ============================================================ */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
          borderRadius: "14px",
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
          color: "#fff",
        }}
      >
        <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 700, mb: 0.5 }}>
          {greetingByHour()}, {staffName || "Staff"}
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.85, mb: 2 }}>
          {dayjs().format("dddd, MMMM D, YYYY")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Chip
            icon={<SchoolIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
            label={`${todayClasses.length} Classes Today`}
            sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 600, fontSize: 12 }}
          />
          <Chip
            icon={<PeopleAltIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
            label={`${statsStudents} Students`}
            sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 600, fontSize: 12 }}
          />
          <Chip
            icon={<AssignmentIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
            label={`${statsPending} Pending Planners`}
            sx={{
              bgcolor: statsPending > 0 ? alpha("#ff9800", 0.85) : alpha("#fff", 0.2),
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
            }}
          />
          <Chip
            icon={<MessageIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
            label={`${statsMessages} Messages`}
            sx={{
              bgcolor: statsMessages > 0 ? alpha("#2196f3", 0.85) : alpha("#fff", 0.2),
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        </Box>
      </Box>

      {/* ============================================================ */}
      {/*  SECTION 2: TWO-COLUMN LAYOUT                                */}
      {/* ============================================================ */}
      <Grid container spacing={2.5}>
        {/* ---- LEFT COLUMN ---- */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Today's Classes */}
            <DashCard title="Today's Classes" icon={<CalendarMonthIcon />}>
              {todayClasses.length === 0 ? (
                <Typography sx={{ color: "text.secondary", fontSize: 13, textAlign: "center", py: 3 }}>
                  No classes scheduled today
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {todayClasses.map((cls: any, idx: number) => {
                    const isCompleted = cls.status === "completed";
                    const isPending = isCompleted && !cls.plannerSubmitted;
                    const isUpcoming = cls.status === "upcoming";
                    return (
                      <Box
                        key={cls.id ?? idx}
                        onClick={() => openAttendanceFor(cls)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: "8px",
                          cursor: "pointer",
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                          border: "1px solid rgba(0,0,0,0.04)",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", minWidth: 100 }}>
                          {formatTime(cls.startTime)} - {formatTime(cls.endTime)}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
                          {cls.studentName}
                        </Typography>
                        <Chip
                          label={cls.subject}
                          size="small"
                          sx={{ fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                        />
                        {isPending && (
                          <Chip
                            label="Planner Pending"
                            size="small"
                            sx={{ fontSize: 11, bgcolor: alpha("#ff9800", 0.12), color: "#e65100", fontWeight: 600 }}
                          />
                        )}
                        {isUpcoming && (
                          <Chip
                            label="Upcoming"
                            size="small"
                            sx={{ fontSize: 11, bgcolor: alpha("#2196f3", 0.12), color: "#1565c0", fontWeight: 600 }}
                          />
                        )}
                        {isCompleted && cls.plannerSubmitted && (
                          <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DashCard>

            {/* Class Records */}
            <DashCard
              title="Class Records"
              icon={<ListAltIcon />}
              action={
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {["today", "week", "month"].map((f) => (
                    <Chip
                      key={f}
                      label={f === "today" ? "Today" : f === "week" ? "This Week" : "This Month"}
                      size="small"
                      onClick={() => setRecordFilter(f)}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        bgcolor: recordFilter === f ? alpha(theme.palette.primary.main, 0.12) : "rgba(0,0,0,0.04)",
                        color: recordFilter === f ? theme.palette.primary.main : "text.secondary",
                        border: recordFilter === f ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : "1px solid transparent",
                      }}
                    />
                  ))}
                </Box>
              }
              noPadding
            >
              {recordsLoading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} height={40} sx={{ mb: 0.5 }} animation="wave" />
                  ))}
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Time</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Student</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Subject</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Attendance</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Planner</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {classRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary", fontSize: 13 }}>
                              No records found
                            </TableCell>
                          </TableRow>
                        ) : (
                          classRecords.map((rec: any, idx: number) => (
                            <TableRow
                              key={rec.id ?? idx}
                              sx={{ "&:hover .row-actions": { opacity: 1 }, "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } }}
                            >
                              <TableCell sx={{ fontSize: 12 }}>{dayjs(rec.date).format("MMM D")}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{formatTime(rec.startTime)}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{rec.studentName}</TableCell>
                              <TableCell>
                                <Chip label={rec.subject} size="small" sx={{ fontSize: 10 }} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={rec.attendance ?? "N/A"}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    bgcolor: rec.attendance === "Present"
                                      ? alpha("#4caf50", 0.1)
                                      : rec.attendance === "Absent"
                                      ? alpha("#f44336", 0.1)
                                      : alpha("#ff9800", 0.1),
                                    color: rec.attendance === "Present"
                                      ? "#2e7d32"
                                      : rec.attendance === "Absent"
                                      ? "#c62828"
                                      : "#e65100",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={rec.plannerSubmitted ? "Submitted" : "Pending"}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    bgcolor: rec.plannerSubmitted ? alpha("#4caf50", 0.1) : alpha("#ff9800", 0.1),
                                    color: rec.plannerSubmitted ? "#2e7d32" : "#e65100",
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Box className="row-actions" sx={{ opacity: 0, transition: "opacity 0.2s", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                                  <Tooltip title="Mark Attendance">
                                    <IconButton size="small" onClick={() => openAttendanceFor(rec)}>
                                      <FactCheckIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Write Planner">
                                    <IconButton size="small" onClick={() => openPlannerFor(rec)}>
                                      <AssignmentIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(classRecordsData?.data as any[])?.length > 10 && (
                    <Box sx={{ textAlign: "center", py: 1.5 }}>
                      <Button size="small" onClick={() => navigate("/class-schedule/list")} sx={{ fontSize: 12 }}>
                        View All Records
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </DashCard>

            {/* Planner Form (collapsible) */}
            <Collapse in={plannerOpen}>
              <DashCard title="Tutoring Planner" icon={<AssignmentIcon />} action={
                <IconButton size="small" onClick={() => setPlannerOpen(false)}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              }>
                {selectedClass && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: "8px" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {dayjs(selectedClass.date).format("MMM D, YYYY")} | {formatTime(selectedClass.startTime)} - {formatTime(selectedClass.endTime)} | {selectedClass.studentName} | {selectedClass.subject}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="School Focus"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={plannerForm.schoolFocus}
                    onChange={(e) => setPlannerForm((p) => ({ ...p, schoolFocus: e.target.value }))}
                  />
                  <TextField
                    label="Assigned Homework"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={plannerForm.assignedHomework}
                    onChange={(e) => setPlannerForm((p) => ({ ...p, assignedHomework: e.target.value }))}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Homework Done?</InputLabel>
                    <Select
                      label="Homework Done?"
                      value={plannerForm.homeworkDone}
                      onChange={(e) => setPlannerForm((p) => ({ ...p, homeworkDone: e.target.value }))}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Excused">Excused</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Challenges & Next Steps"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={plannerForm.challenges}
                    onChange={(e) => setPlannerForm((p) => ({ ...p, challenges: e.target.value }))}
                  />
                  <TextField
                    label="Notes"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={plannerForm.notes}
                    onChange={(e) => setPlannerForm((p) => ({ ...p, notes: e.target.value }))}
                  />
                  <TextField
                    label="Future Plan"
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    value={plannerForm.futurePlan}
                    onChange={(e) => setPlannerForm((p) => ({ ...p, futurePlan: e.target.value }))}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={plannerForm.sendReminder}
                          onChange={(e) => setPlannerForm((p) => ({ ...p, sendReminder: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: 13 }}>Send Reminder</Typography>}
                    />
                    {plannerForm.sendReminder && (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={plannerForm.reminderMethod}
                          onChange={(e) => setPlannerForm((p) => ({ ...p, reminderMethod: e.target.value }))}
                        >
                          <MenuItem value="Email">Email</MenuItem>
                          <MenuItem value="SMS">SMS</MenuItem>
                          <MenuItem value="Both">Both</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleSavePlanner(false)}
                      sx={{ fontSize: 12, textTransform: "none" }}
                    >
                      Save Draft
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleSavePlanner(true)}
                      sx={{ fontSize: 12, textTransform: "none" }}
                    >
                      Submit Planner
                    </Button>
                  </Box>
                </Box>
              </DashCard>
            </Collapse>
          </Box>
        </Grid>

        {/* ---- RIGHT COLUMN ---- */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Weekly Timetable */}
            <DashCard title="This Week's Schedule" icon={<EventNoteIcon />}>
              {timetableLoading ? (
                [...Array(3)].map((_, i) => (
                  <Skeleton key={i} height={36} sx={{ mb: 0.5 }} animation="wave" />
                ))
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {DAYS.map((day) => {
                    const dayClasses: any[] = timetable[day] ?? timetable[day.toLowerCase()] ?? [];
                    if (dayClasses.length === 0) return null;
                    return (
                      <Box key={day}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", mb: 0.5 }}>
                          {day}
                        </Typography>
                        {dayClasses.map((cls: any, idx: number) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              pl: 1.5,
                              py: 0.75,
                              borderLeft: `3px solid ${cls.color ?? theme.palette.primary.main}`,
                              mb: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: cls.plannerSubmitted ? "#4caf50" : "#ff9800",
                                flexShrink: 0,
                              }}
                            />
                            <Typography sx={{ fontSize: 11, color: "text.secondary", minWidth: 55 }}>
                              {formatTime(cls.startTime)}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 500, flex: 1 }}>
                              {cls.studentName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                  {Object.keys(timetable).length === 0 && (
                    <Typography sx={{ fontSize: 13, color: "text.secondary", textAlign: "center", py: 2 }}>
                      No classes this week
                    </Typography>
                  )}
                </Box>
              )}
            </DashCard>

            {/* Quick Actions */}
            <DashCard title="Quick Actions">
              <Grid container spacing={1}>
                {[
                  { label: "Mark Attendance", path: "/attendance-record/list", icon: <FactCheckIcon sx={{ fontSize: 20 }} /> },
                  { label: "View Messages", path: "/message/list", icon: <MessageIcon sx={{ fontSize: 20 }} /> },
                  { label: "View Schedule", path: "/class-schedule/list", icon: <CalendarMonthIcon sx={{ fontSize: 20 }} /> },
                  { label: "Student List", path: "/student/list", icon: <PeopleAltIcon sx={{ fontSize: 20 }} /> },
                ].map((item) => (
                  <Grid item xs={6} key={item.label}>
                    <Box
                      onClick={() => navigate(item.path)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.75,
                        p: 1.5,
                        borderRadius: "10px",
                        cursor: "pointer",
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: "1px solid rgba(0,0,0,0.04)",
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                        transition: "background 0.2s",
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, textAlign: "center" }}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </DashCard>

            {/* Pending Planners */}
            <DashCard
              title="Pending Planners"
              icon={<WarningAmberIcon />}
              action={
                statsPending > 0 ? (
                  <Badge badgeContent={statsPending} color="warning" sx={{ "& .MuiBadge-badge": { fontSize: 10 } }} />
                ) : undefined
              }
            >
              {pendingPlanners.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: "#4caf50", mb: 0.5 }} />
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    All planners submitted
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {pendingPlanners.map((pp: any, idx: number) => (
                    <Box
                      key={pp.id ?? idx}
                      onClick={() => openPlannerFor(pp)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: "8px",
                        cursor: "pointer",
                        borderLeft: `3px solid #ff9800`,
                        bgcolor: alpha("#ff9800", 0.03),
                        "&:hover": { bgcolor: alpha("#ff9800", 0.08) },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                          {pp.studentName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          {dayjs(pp.date).format("MMM D")} | {formatTime(pp.startTime)} - {formatTime(pp.endTime)}
                        </Typography>
                      </Box>
                      <AccessTimeIcon sx={{ fontSize: 16, color: "#ff9800" }} />
                    </Box>
                  ))}
                </Box>
              )}
            </DashCard>
          </Box>
        </Grid>
      </Grid>

      {/* ============================================================ */}
      {/*  ATTENDANCE MODAL                                            */}
      {/* ============================================================ */}
      <Dialog
        open={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
          Class Attendance
          <IconButton
            size="small"
            onClick={() => setAttendanceOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {attendanceClass && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: "8px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {attendanceClass.subject} | {dayjs(attendanceClass.date).format("MMM D, YYYY")}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {formatTime(attendanceClass.startTime)} - {formatTime(attendanceClass.endTime)}
              </Typography>
            </Box>
          )}
          {studentsLoading ? (
            [...Array(3)].map((_, i) => (
              <Skeleton key={i} height={44} sx={{ mb: 0.5 }} animation="wave" />
            ))
          ) : classStudents.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "text.secondary", py: 3, fontSize: 13 }}>
              No students found for this class
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {classStudents.map((stu: any) => (
                <Box
                  key={stu.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.25,
                    borderRadius: "8px",
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    {stu.studentName}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={attendanceMap[stu.id] ?? stu.currentStatus ?? ""}
                    onChange={(_, val) => {
                      if (val !== null) {
                        setAttendanceMap((prev) => ({ ...prev, [stu.id]: val }));
                      }
                    }}
                    sx={{ "& .MuiToggleButton-root": { fontSize: 10, px: 1.25, py: 0.4, textTransform: "none" } }}
                  >
                    <ToggleButton value="Present" sx={{ "&.Mui-selected": { bgcolor: alpha("#4caf50", 0.15), color: "#2e7d32" } }}>
                      Present
                    </ToggleButton>
                    <ToggleButton value="Absent" sx={{ "&.Mui-selected": { bgcolor: alpha("#f44336", 0.15), color: "#c62828" } }}>
                      Absent
                    </ToggleButton>
                    <ToggleButton value="Late" sx={{ "&.Mui-selected": { bgcolor: alpha("#ff9800", 0.15), color: "#e65100" } }}>
                      Late
                    </ToggleButton>
                    <ToggleButton value="Excused" sx={{ "&.Mui-selected": { bgcolor: alpha("#2196f3", 0.15), color: "#1565c0" } }}>
                      Excused
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            size="small"
            onClick={() => setAttendanceOpen(false)}
            sx={{ fontSize: 12, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveAttendance}
            sx={{ fontSize: 12, textTransform: "none" }}
          >
            Save Attendance
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffDashboard;
